import { useCallback } from 'react';
import { Dimensions, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

import { FlingStack } from '@/components/suggestions/stacked-cards';
import { SuggestionStackCard } from '@/components/suggestions/SuggestionStackCard';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { AISuggestion } from '@/types/ai-suggestions';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const CARD_WIDTH = SCREEN_WIDTH - 64;
const CARD_HEIGHT = SCREEN_HEIGHT * 0.35;
const SWIPE_THRESHOLD = SCREEN_WIDTH * 0.3;

type SuggestionsFlingStackProps = {
  suggestions: AISuggestion[];
  onAddToCalendar: (suggestion: AISuggestion) => void;
  onAddToTodo: (suggestion: AISuggestion) => Promise<boolean>;
  onDismiss: (suggestion: AISuggestion) => void;
  onViewHistory: () => void;
};

/**
 * SuggestionsFlingStack - FlingStack wrapper with horizontal swipe-to-dismiss.
 * Vertical fling navigates between cards, horizontal swipe dismisses.
 */
export function SuggestionsFlingStack({
  suggestions,
  onAddToCalendar,
  onAddToTodo,
  onDismiss,
  onViewHistory,
}: SuggestionsFlingStackProps) {
  if (suggestions.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <View style={styles.emptyBackdrop}>
          <Ionicons name="checkmark-circle-outline" size={48} color="#FFFFFF" style={styles.emptyIcon} />
          <Text style={[styles.emptyTitle, { fontFamily: getBodyFont() }]}>
            All caught up!
          </Text>
          <Text style={[styles.emptySubtitle, { fontFamily: getBodyFont() }]}>
            No more suggestions right now
          </Text>
          <Pressable
            style={styles.historyButton}
            onPress={onViewHistory}
          >
            <Ionicons name="time-outline" size={18} color="#FFFFFF" />
            <Text style={[styles.historyButtonText, { fontFamily: getBodyFont() }]}>
              View previous suggestions
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} pointerEvents="box-none">
      <FlingStack
        data={suggestions}
        visibleCount={3}
        cardWidth={CARD_WIDTH}
        cardHeight={CARD_HEIGHT}
        useBlur={false}
        wrapperStyle={styles.stackWrapper}
        cardContainerStyle={styles.cardContainer}
        renderItem={({ item }) => (
          <SwipeableSuggestionCard
            suggestion={item}
            onAddToCalendar={onAddToCalendar}
            onAddToTodo={onAddToTodo}
            onDismiss={onDismiss}
          />
        )}
      />
    </View>
  );
}

type SwipeableSuggestionCardProps = {
  suggestion: AISuggestion;
  onAddToCalendar: (suggestion: AISuggestion) => void;
  onAddToTodo: (suggestion: AISuggestion) => Promise<boolean>;
  onDismiss: (suggestion: AISuggestion) => void;
};

/**
 * SwipeableSuggestionCard - Wraps SuggestionStackCard with horizontal pan gesture
 * for swipe-to-dismiss functionality.
 */
function SwipeableSuggestionCard({
  suggestion,
  onAddToCalendar,
  onAddToTodo,
  onDismiss,
}: SwipeableSuggestionCardProps) {
  const translateX = useSharedValue(0);
  const opacity = useSharedValue(1);

  const handleDismiss = useCallback(() => {
    onDismiss(suggestion);
  }, [suggestion, onDismiss]);

  const panGesture = Gesture.Pan()
    .activeOffsetX([-20, 20])
    .failOffsetY([-10, 10])
    .onUpdate((event) => {
      translateX.value = event.translationX;
      opacity.value = 1 - Math.abs(event.translationX) / SCREEN_WIDTH;
    })
    .onEnd((event) => {
      if (Math.abs(event.translationX) > SWIPE_THRESHOLD) {
        const direction = event.translationX > 0 ? 1 : -1;
        translateX.value = withTiming(direction * SCREEN_WIDTH, { duration: 200 });
        opacity.value = withTiming(0, { duration: 200 }, () => {
          runOnJS(handleDismiss)();
        });
      } else {
        translateX.value = withSpring(0);
        opacity.value = withSpring(1);
      }
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
    opacity: opacity.value,
  }));

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View style={[styles.swipeableCard, animatedStyle]}>
        <SuggestionStackCard
          suggestion={suggestion}
          onAddToCalendar={onAddToCalendar}
          onAddToTodo={onAddToTodo}
          onDismiss={onDismiss}
        />
      </Animated.View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stackWrapper: {
    height: CARD_HEIGHT + 60,
  },
  cardContainer: {
    borderRadius: 16,
  },
  swipeableCard: {
    width: '100%',
    height: '100%',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 80,
  },
  emptyBackdrop: {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    borderRadius: 20,
    paddingVertical: Spacing.xl,
    paddingHorizontal: Spacing.xxl,
    alignItems: 'center',
    gap: Spacing.xs,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
  },
  emptyIcon: {
    marginBottom: Spacing.xs,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  emptySubtitle: {
    fontSize: 15,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  historyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
    marginTop: Spacing.md,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  historyButtonText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
